<template>
  <div class="min-h-screen" :class="isDark ? 'bg-gray-900' : 'bg-gray-50'">
    <div class="max-w-6xl mx-auto px-6 py-12">
      <div class="text-center mb-12">
        <h1 class="text-3xl font-bold" :class="isDark ? 'text-white' : 'text-gray-800'">我的博客</h1>
        <p class="mt-2" :class="isDark ? 'text-gray-400' : 'text-gray-500'">分享技术见解和开发经验</p>
        <div class="w-20 h-1 bg-green-500 mx-auto mt-4"></div>
      </div>

      <div class="flex flex-wrap justify-center gap-2 mb-8">
        <button @click="activeTag = 'all'" 
                class="px-4 py-2 rounded-full text-sm font-medium transition-colors"
                :class="activeTag === 'all' ? 'bg-green-600 text-white' : (isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100')">
          全部
        </button>
        <button v-for="tag in tags" :key="tag" @click="activeTag = tag" 
                class="px-4 py-2 rounded-full text-sm font-medium transition-colors"
                :class="activeTag === tag ? 'bg-green-600 text-white' : (isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100')">
          {{ tag }}
        </button>
      </div>

      <div v-if="isLoggedIn" class="mb-8">
        <button @click="openEditor()" 
                class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors">
          写文章
        </button>
      </div>

      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="post in filteredPosts" 
          :key="post.id"
          class="rounded-xl overflow-hidden border transition-all group cursor-pointer"
          :class="isDark ? 'bg-gray-800 border-gray-700 hover:border-green-500/50' : 'bg-white border-gray-200 hover:border-green-500/50 shadow-sm'"
          @click="viewPost(post)"
        >
          <div class="p-6">
            <div class="flex items-center justify-between mb-4">
              <span class="px-3 py-1 text-xs rounded-full"
                    :class="isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'">
                {{ post.category }}
              </span>
              <span class="text-xs" :class="isDark ? 'text-gray-500' : 'text-gray-400'">{{ post.date }}</span>
            </div>
            <h3 class="text-xl font-semibold mb-3 group-hover:text-green-400 transition-colors"
                :class="isDark ? 'text-white' : 'text-gray-800'">
              {{ post.title }}
            </h3>
            <p class="text-sm line-clamp-3 mb-4" :class="isDark ? 'text-gray-400' : 'text-gray-500'">{{ post.excerpt }}</p>
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <img :src="post.avatar" :alt="post.author" class="w-6 h-6 rounded-full" />
                <span class="text-sm" :class="isDark ? 'text-gray-400' : 'text-gray-500'">{{ post.author }}</span>
              </div>
              <div class="flex items-center space-x-3 text-sm" :class="isDark ? 'text-gray-500' : 'text-gray-400'">
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {{ post.views }}
                </span>
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {{ post.comments }}
                </span>
              </div>
            </div>
            <div v-if="isLoggedIn" class="flex space-x-2 mt-4 pt-4 border-t" :class="isDark ? 'border-gray-700' : 'border-gray-200'">
              <button @click.stop="openEditor(post)" 
                      class="flex-1 px-3 py-1.5 text-xs rounded-lg transition-colors"
                      :class="isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'">
                编辑
              </button>
              <button @click.stop="deletePost(post.id)" 
                      class="flex-1 px-3 py-1.5 text-xs rounded-lg transition-colors"
                      :class="isDark ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400' : 'bg-red-50 hover:bg-red-100 text-red-600'">
                删除
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useTheme } from '../composables/useTheme'
import { useAuth } from '../composables/useAuth'
import { useRouter } from 'vue-router'
import { marked } from 'marked'
import hljs from 'highlight.js'
import 'highlight.js/styles/github-dark.css'
import katex from 'katex'
import 'katex/dist/katex.min.css'

marked.setOptions({
  breaks: true,
  gfm: true
})

const renderer = new marked.Renderer()

renderer.code = function({ text, lang }) {
  const language = lang && hljs.getLanguage(lang) ? lang : 'plaintext'
  const highlighted = hljs.highlight(text, { language }).value
  return `<pre><code class="hljs language-${language}">${highlighted}</code></pre>`
}

renderer.text = function({ raw }) {
  return raw
    .replace(/\$\$(.*?)\$\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: true,
          throwOnError: false
        })
      } catch {
        return match
      }
    })
    .replace(/\$(.*?)\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: false,
          throwOnError: false
        })
      } catch {
        return match
      }
    })
}

marked.use({ renderer })

export default {
  name: 'Blog',
  setup() {
    const { isDark } = useTheme()
    const { isLoggedIn } = useAuth()
    const router = useRouter()
    
    const activeTag = ref('all')
    const tags = ['Vue.js', 'React', 'Node.js', 'Python', '其他']
    
    const posts = ref([
      {
        id: 1,
        title: 'Vue 3 Composition API 完全指南',
        excerpt: '深入理解 Vue 3 的 Composition API，包括 ref、reactive、computed 等核心概念...',
        content: `## 什么是 Composition API？

Composition API 是 Vue 3 引入的一组基于函数的 API，它允许我们使用函数来组织组件逻辑，而不是选项对象。

## 核心概念

### ref

创建响应式引用：

\`\`\`javascript
import { ref } from 'vue'

const count = ref(0)
console.log(count.value) // 0
\`\`\`

### reactive

创建响应式对象：

\`\`\`javascript
import { reactive } from 'vue'

const state = reactive({
  name: 'PANZHIJIE',
  age: 28
})
\`\`\`

### computed

创建计算属性：

\`\`\`javascript
import { computed } from 'vue'

const doubleCount = computed(() => count.value * 2)
\`\`\`

### watch

监听响应式变化：

\`\`\`javascript
import { watch } from 'vue'

watch(count, (newVal, oldVal) => {
  console.log(\`count changed from \${oldVal} to \${newVal}\`)
})
\`\`\``,
        category: 'Vue.js',
        date: '2024-01-15',
        author: '潘志捷',
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=panzhijie',
        views: 2340,
        comments: 45
      },
      {
        id: 2,
        title: 'React Server Components 初探',
        excerpt: '探索 React Server Components 的工作原理，了解如何利用它提升应用性能...',
        content: `## React Server Components 简介

React Server Components 是 React 团队推出的一种新的组件类型，它允许组件在服务器端渲染。

## 主要优势

- 减少客户端 JavaScript 体积
- 直接访问服务器数据
- 更好的 SEO

## 使用方式

\`\`\`jsx
async function BlogPost({ id }) {
  const post = await db.posts.findOne({ id })
  return <Post content={post.content} />
}
\`\`\``,
        category: 'React',
        date: '2024-01-12',
        author: '潘志捷',
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=panzhijie',
        views: 1890,
        comments: 32
      },
      {
        id: 3,
        title: 'Node.js 性能优化实战',
        excerpt: '分享 Node.js 应用性能优化的实用技巧，包括内存管理、异步处理...',
        content: `## 性能优化策略

Node.js 性能优化涉及多个方面，包括内存管理、异步处理、缓存策略等。

## 常见优化手段

### 使用 Stream 处理大文件

\`\`\`javascript
const fs = require('fs')
const { pipeline } = require('stream/promises')

async function processFile() {
  await pipeline(
    fs.createReadStream('input.txt'),
    // 转换流
    fs.createWriteStream('output.txt')
  )
}
\`\`\`

### 合理使用缓存

- 使用 Redis 缓存热点数据
- 实现数据过期策略

### 避免阻塞事件循环

- 使用异步 I/O
- 避免 CPU 密集型操作`,
        category: 'Node.js',
        date: '2024-01-10',
        author: '潘志捷',
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=panzhijie',
        views: 3120,
        comments: 58
      },
      {
        id: 4,
        title: 'Python 异步编程入门',
        excerpt: '学习 Python 异步编程，掌握 asyncio 的核心概念和使用方法...',
        content: `## 异步编程基础

Python 3.5+ 引入了 async/await 语法，使得异步编程更加简洁直观。

## asyncio 核心概念

### 协程 (Coroutine)

\`\`\`python
async def fetch_data():
    await asyncio.sleep(1)
    return {'data': 'hello'}
\`\`\`

### 事件循环 (Event Loop)

事件循环是 asyncio 的核心，负责执行协程、处理 I/O 操作等。

### 任务 (Task)

任务是对协程的封装，可以并发执行多个任务。

\`\`\`python
async def main():
    task1 = asyncio.create_task(fetch_data())
    task2 = asyncio.create_task(fetch_data())
    
    await task1
    await task2
\`\`\``,
        category: 'Python',
        date: '2024-01-08',
        author: '潘志捷',
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=panzhijie',
        views: 1560,
        comments: 28
      },
      {
        id: 5,
        title: 'TypeScript 高级类型技巧',
        excerpt: '深入学习 TypeScript 的高级类型系统，提升代码类型安全性...',
        content: `## 高级类型

TypeScript 提供了丰富的高级类型特性，可以帮助我们编写更安全、更灵活的代码。

## 常用高级类型

### 泛型 (Generics)

\`\`\`typescript
function identity<T>(arg: T): T {
    return arg
}
\`\`\`

### 条件类型 (Conditional Types)

\`\`\`typescript
type IsString<T> = T extends string ? true : false
\`\`\`

### 映射类型 (Mapped Types)

\`\`\`typescript
type Readonly<T> = {
    readonly [P in keyof T]: T[P]
}
\`\`\``,
        category: '其他',
        date: '2024-01-05',
        author: '潘志捷',
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=panzhijie',
        views: 1980,
        comments: 41
      },
      {
        id: 6,
        title: 'Docker 容器化实践',
        excerpt: '掌握 Docker 容器化技术，实现应用的快速部署和运行...',
        content: `## Docker 基础

Docker 是一个开源的容器化平台，可以帮助我们轻松打包、部署和运行应用。

## 核心概念

### 镜像 (Image)

镜像是一个只读的模板，包含运行应用所需的所有文件和依赖。

### 容器 (Container)

容器是镜像的运行实例，可以被创建、启动、停止、删除。

### Dockerfile

Dockerfile 是一个文本文件，包含构建镜像所需的指令。

\`\`\`dockerfile
FROM node:18-alpine
WORKDIR /app
COPY package*.json ./
RUN npm install
COPY . .
EXPOSE 3000
CMD ["npm", "start"]
\`\`\``,
        category: '其他',
        date: '2024-01-03',
        author: '潘志捷',
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=panzhijie',
        views: 2450,
        comments: 36
      }
    ])
    
    const filteredPosts = computed(() => {
      if (activeTag.value === 'all') {
        return posts.value
      }
      return posts.value.filter(post => post.category === activeTag.value)
    })
    
    const renderMarkdown = (content) => {
      if (!content) return '<p class="text-gray-500">暂无内容</p>'
      return marked.parse(content)
    }
    
    const viewPost = (post) => {
      router.push(`/blog/${post.id}`)
    }
    
    const openEditor = (post = null) => {
      if (post) {
        router.push(`/blog/edit/${post.id}`)
      } else {
        router.push('/blog/edit')
      }
    }
    
    const deletePost = (id) => {
      if (confirm('确定要删除这篇文章吗？')) {
        posts.value = posts.value.filter(p => p.id !== id)
      }
    }
    
    return { 
      isDark, 
      isLoggedIn,
      activeTag, 
      tags,
      posts,
      filteredPosts,
      renderMarkdown,
      viewPost,
      openEditor,
      deletePost
    }
  }
}
</script>